// WHAT: TimeTracQ Settings View Component
// WHY:  Tabbed interface for TimeTracQ configuration (settings, workspaces, debug)
// DOMAIN: Settings
// DEPENDENCIES: UI components, TimeTracQ sub-components, Lucide icons
// RISKS:
//   - NOTE: Tab state is managed locally (no persistence)
//   - NOTE: Tab names are hardcoded strings (no type safety)

import { Clock, Settings, FolderOpen, Bug, Database } from "lucide-react";
import { useState } from "react";

import { Card, Button, Stack, Heading } from "@/components/ui";

import {
  TimeTracQSettings,
  TimeTracQWorkspaceManagement,
  TimeTracQDebug,
  TimeTracQWorkspaceDebug,
} from "./timetracqsettings";

/**
 * WHAT:
 *   Settings view component with tabbed interface for TimeTracQ configuration.
 *
 * WHY:
 *   Provides organized access to different TimeTracQ settings sections.
 *
 * BEHAVIOR:
 *   - Manages active tab state (settings, workspaces, debug, workspace-debug)
 *   - Displays header section with clock icon and description
 *   - Shows tab navigation buttons with icons
 *   - Renders appropriate component based on active tab
 *   - Tab buttons change variant based on active state
 *
 * RISKS:
 *   - NOTE: Tab state is not persisted (resets on page reload)
 *   - NOTE: Tab names are hardcoded strings (no type safety)
 *   - NOTE: No validation that tab components exist
 *
 * @returns {JSX.Element} TimeTracQ settings view component
 *
 */
export function TimeTracQSettingsView() {
  const [activeTab, setActiveTab] = useState<
    "debug" | "settings" | "workspace-debug" | "workspaces"
  >("settings");

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <Card className="bg-slate-800/30 border-slate-700/50" padding="lg" variant="default">
        <Stack align="center" direction="row" gap={4}>
          <div className="p-3 bg-tracq-bright-blue/10 rounded-lg">
            <Clock className="w-6 h-6 text-tracq-bright-blue" />
          </div>
          <Stack direction="col" gap={1}>
            <Heading className="text-xl font-semibold text-white" level={2}>
              TimeTracQ Configuration
            </Heading>
            <p className="text-slate-300">
              Manage your TimeTracQ workspace settings and time tracking preferences for optimal
              productivity.
            </p>
          </Stack>
        </Stack>
      </Card>

      {/* Tab Navigation */}
      <Card className="bg-slate-800/50 border-slate-700/50 p-0" variant="default">
        <div className="border-b border-slate-700/50">
          <nav className="-mb-px flex space-x-8 px-6">
            <Button
              className="border-b-2 border-transparent rounded-none"
              onClick={() => {
                setActiveTab("settings");
              }}
              size="md"
              variant={activeTab === "settings" ? "primary" : "ghost"}
            >
              <Settings className="w-4 h-4 mr-2" />
              Settings
            </Button>
            <Button
              className="border-b-2 border-transparent rounded-none"
              onClick={() => {
                setActiveTab("workspaces");
              }}
              size="md"
              variant={activeTab === "workspaces" ? "primary" : "ghost"}
            >
              <FolderOpen className="w-4 h-4 mr-2" />
              Workspace Management
            </Button>
            <Button
              className="border-b-2 border-transparent rounded-none"
              onClick={() => {
                setActiveTab("debug");
              }}
              size="md"
              variant={activeTab === "debug" ? "primary" : "ghost"}
            >
              <Bug className="w-4 h-4 mr-2" />
              Debug
            </Button>
            <Button
              className="border-b-2 border-transparent rounded-none"
              onClick={() => {
                setActiveTab("workspace-debug");
              }}
              size="md"
              variant={activeTab === "workspace-debug" ? "primary" : "ghost"}
            >
              <Database className="w-4 h-4 mr-2" />
              Workspace Debug
            </Button>
          </nav>
        </div>
      </Card>

      {/* Tab Content */}
      {activeTab === "settings" && <TimeTracQSettings />}
      {activeTab === "workspaces" && <TimeTracQWorkspaceManagement />}
      {activeTab === "debug" && <TimeTracQDebug />}
      {activeTab === "workspace-debug" && <TimeTracQWorkspaceDebug />}
    </div>
  );
}
